import { TableElementController, VisualController } from '@sas-dvr/ltjs-bird';
import { TableState } from '@sas-dvr/internal-va-react-core/store/birdElement/tableState';
import { VisualElementControllerLoader } from './LoaderFactory';
import { map, filter, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  DataGridPickResultEntry,
  VisualizationManager,
  PickEntryType,
} from '@sas-dvr/ltjs-commons';
import { MouseUpEventArgs } from '@sas-dvr/ltjs-commons/gestures/GestureTypes';
import { filterNulls } from '../util';
import { toggleSortDirection } from '../util/sortUtils';
import { filterEntriesByType } from '../util/pickUtils';
import { getMaximizeObservables } from '../util/maximizeUtils';
import { EMPTY, merge } from 'rxjs';
import { loadVisualization } from './loadVisualization';
import { getManageColumnsObservables } from '../util/manageColumnsUtils';
import { globalFeatures$ } from '../../config/globalFeatures';

export const loadTableElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(visualElementController instanceof TableElementController)) {
    throw new Error('Expected the controller to be a TableElementController');
  }

  const visualId = deps.getKey(visualElementController);
  const contentId$ = state$.pipe(
    map((aState) => aState.birdElements.Table[visualId]),
    filter(
      (myState): myState is Exclude<Required<typeof myState>, undefined> =>
        myState !== undefined && myState.contentId !== undefined
    ),
    distinctUntilChanged(),
    map((myState) => myState.contentId)
  );

  const visualizationManager = new VisualizationManager();
  const loadVisualElement$ = loadVisualization(
    visualController,
    visualElementController,
    unload$,
    myActions$,
    state$,
    deps,
    {
      create: () => TableState.actions.add(visualId, {}),
      delete: () => TableState.actions.delete(visualId),
    },
    { contentId$, visualizationManager }
  );

  visualizationManager.addGestureOverride('mouseUp', (gesture: MouseUpEventArgs) => {
    onMouseUp(visualElementController, visualizationManager, gesture);
  });

  if (!visualController) {
    // Sorting has to be disabled on supplementary table to avoid sorting of graph since they share the same query.
    // Copied from old VAViewerHTMLComponent code.
    visualElementController.setSortEnabled(false);
    visualElementController.updateViewData();
  }

  const handleMaximize$ = getMaximizeObservables(
    visualId,
    visualController,
    visualElementController,
    state$,
    unload$,
    {
      preferOverflowMenu: true,
    }
  );

  const inComposite =
    visualController instanceof VisualController && visualController.getCompositeParent() !== null;
  const handleManageColumns$ = globalFeatures$.pipe(
    map((feature) => feature.manageColumns),
    distinctUntilChanged(),
    switchMap((manageColumns) =>
      manageColumns && !inComposite
        ? getManageColumnsObservables(
            visualId,
            visualController,
            visualElementController,
            state$,
            myActions$
          )
        : EMPTY
    )
  );

  return merge(loadVisualElement$, handleMaximize$, handleManageColumns$);
};

// Sort column if user clicks on column header
function onMouseUp(
  controller: TableElementController,
  visualizationManager: VisualizationManager,
  gesture: MouseUpEventArgs
) {
  if (gesture.localX === undefined || gesture.localY === undefined) return;

  const pickResults = visualizationManager.getPickResults(
    gesture.localX,
    gesture.localY,
    false,
    false
  );
  const entry = filterEntriesByType(pickResults, DataGridPickResultEntry)[0];

  if (
    entry &&
    entry.getType() === PickEntryType.DATAGRID_COLUMN_HEADER &&
    controller.canSort(entry.getColumnIndex())
  ) {
    const sortController = controller.getTooltipSortControllerWithAllSortItems(pickResults);
    if (sortController) {
      // Get sort item. There should be only one sortItem if a column header was clicked.
      const sortItem = filterNulls(sortController.getSortItems())[0];
      if (sortItem) {
        // If control key is pressed, the user intends to set a secondary sort.
        const isPrimary = !gesture.ctrlKey;
        toggleSortDirection(sortController, sortItem, isPrimary);
      }
    }
  }
}
